import moment from "moment";
import styled from "styled-components";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { CHART_COLORS, formatMetricValue, formatYAxisValue, getTimeFormat } from "./formatters";
import {useCallback, useMemo} from "react";


const ChartContainer = styled.div<{ height?: number }>`
    height: ${(props) => props.height || 300}px;
    margin-top: 12px;
`;

export interface TimeseriesDataPoint {
    timestamp: number;
    [key: string]: number;
}

export interface ChartLine {
    dataKey: string;
    color?: string;
    name?: string;
}

interface MetricsLineChartProps {
    data: TimeseriesDataPoint[];
    lines: ChartLine[];
    height?: number;
    categoryHint?: string;
    showLegend?: boolean;
}

function computeTicks(data: TimeseriesDataPoint[], count: number = 5): number[] {
    if (data.length === 0) return [];
    if (data.length <= count) return data.map((d) => d.timestamp);

    const timestamps = data.map((d) => d.timestamp).sort((a, b) => a - b);
    const min = timestamps[0];
    const max = timestamps[timestamps.length - 1];
    const step = (max - min) / (count - 1);

    const ticks: number[] = [];
    for (let i = 0; i < count; i++) {
        ticks.push(Math.round(min + step * i));
    }
    return ticks;
}

export default function MetricsLineChart({
    data,
    lines,
    height = 300,
    categoryHint = "",
    showLegend = false,
}: MetricsLineChartProps) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const ticks = useMemo(() => computeTicks(data, 5), [data]);
    const timeFormat = useMemo(() => getTimeFormat(data.map((d) => d.timestamp)), [data]);

    const tooltipFormatter = useCallback(
        (value: number, name: string) => [formatMetricValue(value, name), name],
        [],
    );

    const xAxisTickFormatter = useCallback(
        (ts: number) => moment(ts).tz(tz).format(timeFormat),
        [tz, timeFormat],
    );

    const yAxisTickFormatter = useCallback(
        (value: number) => formatYAxisValue(value, categoryHint),
        [categoryHint],
    );

    const labelFormatter = useCallback(
        (ts: number) => moment(ts).tz(tz).format("MMM D, HH:mm:ss"),
        [tz],
    );

    return (
        <ChartContainer height={height}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ left: 5, right: 15, top: 5, bottom: 5 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <Tooltip labelFormatter={labelFormatter} formatter={tooltipFormatter} />
                    <XAxis
                        dataKey="timestamp"
                        tickFormatter={xAxisTickFormatter}
                        style={{ fontSize: "11px" }}
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        ticks={ticks}
                    />
                    <YAxis
                        style={{ fontSize: "11px" }}
                        tickFormatter={yAxisTickFormatter}
                        width={60}
                    />
                    {lines.map((line, index) => (
                        <Line
                            key={line.dataKey}
                            type="monotone"
                            dataKey={line.dataKey}
                            name={line.name || line.dataKey}
                            stroke={line.color || CHART_COLORS[index % CHART_COLORS.length]}
                            dot={false}
                            isAnimationActive={false}
                            strokeWidth={2}
                        />
                    ))}
                    {showLegend && (
                        <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            iconSize={10}
                            wrapperStyle={{ fontSize: "12px" }}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}
